import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  getUser,
  getToken,
  getRefreshToken,
  authHeaders,
  logout,
  IDENTITY_HUB_URL,
  IDENTITY_HUB_API,
} from "../auth";
import "./AppHeader.css";

/* ── AppHeader ─────────────────────────────────────────────────────────────
   Slim enterprise top bar shared across the Identity Hub module suite.
   Right-aligned: [ app switcher (waffle) ] [ user avatar / sign-in ].
   Left: the Siragu butterfly + wordmark. Mirrors the Purchase app header.
   ────────────────────────────────────────────────────────────────────────── */

/* Initials from a name/email, e.g. "Jega Needhi" -> "JN". */
function initialsOf(user) {
  if (!user) return "?";
  const src = (user.full_name || user.email || "").trim();
  if (!src) return "?";
  if (src.includes("@") && !src.includes(" ")) {
    return src[0].toUpperCase();
  }
  const parts = src.split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] || "";
  const last = parts.length > 1 ? parts[parts.length - 1][0] : "";
  return (first + last).toUpperCase() || "?";
}

function readLS(key) {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

/* Roles allowed to open AI Settings. The backend already blocks non-admin
   writes; this hides the gear/menu item in the UI for everyone else. */
const ADMIN_ROLES = ["institution_admin", "admin", "super_admin"];
function isAdminUser() {
  const u = getUser();
  const role = u && u.role ? String(u.role).toLowerCase() : "";
  return ADMIN_ROLES.includes(role);
}

function AppHeader({ onOpenSettings, selectedApp, onSelectApp }) {
  const user = getUser();
  const showSettings = isAdminUser();
  const [dark, setDark] = useState(false);
  const instLogo = readLS("institution_logo");
  const instName = readLS("institution_name");
  const toggleTheme = () => {
    setDark((d) => {
      const next = !d;
      try {
        document.documentElement.classList.toggle("siragu-dark", next);
      } catch {
        /* ignore */
      }
      return next;
    });
  };
  const [waffleOpen, setWaffleOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [apps, setApps] = useState([]);
  const [appsLoading, setAppsLoading] = useState(false);
  const [appsError, setAppsError] = useState("");

  // Module selector: lets the admin tell Siragu "I'm asking about app X" for the
  // rest of this session, since the standalone chat page (unlike being opened inside a
  // specific ERP screen) has no embedded-page context to infer that from automatically.
  const [moduleOpen, setModuleOpen] = useState(false);
  const [moduleApps, setModuleApps] = useState([]);
  const [moduleAppsLoading, setModuleAppsLoading] = useState(false);
  const [moduleAppsError, setModuleAppsError] = useState("");

  const waffleRef = useRef(null);
  const menuRef = useRef(null);
  const moduleRef = useRef(null);

  /* Close popovers on outside click / Escape. */
  useEffect(() => {
    const onDocClick = (e) => {
      if (waffleRef.current && !waffleRef.current.contains(e.target)) {
        setWaffleOpen(false);
      }
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
      if (moduleRef.current && !moduleRef.current.contains(e.target)) {
        setModuleOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") {
        setWaffleOpen(false);
        setMenuOpen(false);
        setModuleOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  /* Fetch the apps this admin can access, for the module selector — same source as the
     waffle switcher (Identity Hub's own app-mapping list), but WITHOUT filtering out the
     current app: selecting "this module" is a valid, common choice here. */
  const loadModuleApps = useCallback(async () => {
    if (!user || !user.id || !getToken()) {
      setModuleApps([]);
      setModuleAppsError("no-token");
      return;
    }
    setModuleAppsLoading(true);
    setModuleAppsError("");
    try {
      const res = await fetch(
        `${IDENTITY_HUB_API}/auth/users/${user.id}/apps`,
        { headers: { ...authHeaders() } }
      );
      if (!res.ok) throw new Error(`Status ${res.status}`);
      const data = await res.json();
      const mappings = (data && data.payload && data.payload.mappings) || [];
      setModuleApps(mappings.filter((m) => m && m.is_active));
    } catch {
      setModuleAppsError("fetch-failed");
      setModuleApps([]);
    } finally {
      setModuleAppsLoading(false);
    }
  }, [user]);

  const toggleModule = () => {
    const next = !moduleOpen;
    setModuleOpen(next);
    setWaffleOpen(false);
    setMenuOpen(false);
    if (next) loadModuleApps();
  };

  const pickModule = (app) => {
    if (onSelectApp) {
      onSelectApp(app ? { id: app.app_id, name: app.app_name } : null);
    }
    setModuleOpen(false);
  };

  /* Fetch this user's other apps from the Identity Hub. */
  const loadApps = useCallback(async () => {
    if (!user || !user.id || !getToken()) {
      setApps([]);
      setAppsError("no-token");
      return;
    }
    setAppsLoading(true);
    setAppsError("");
    try {
      const res = await fetch(
        `${IDENTITY_HUB_API}/auth/users/${user.id}/apps`,
        { headers: { ...authHeaders() } }
      );
      if (!res.ok) throw new Error(`Status ${res.status}`);
      const data = await res.json();
      const mappings =
        (data && data.payload && data.payload.mappings) || [];
      const origin = window.location.origin;
      const mapped = mappings
        .filter((m) => m && m.is_active)
        .map((m) => ({
          ...m,
          resolvedUrl: (m.app_url || "").replace(
            "[appHost]",
            window.location.hostname
          ),
        }))
        // Hide the current app (same origin).
        .filter((m) => {
          try {
            return new URL(m.resolvedUrl).origin !== origin;
          } catch {
            return true;
          }
        });
      setApps(mapped);
    } catch {
      setAppsError("fetch-failed");
      setApps([]);
    } finally {
      setAppsLoading(false);
    }
  }, [user]);

  const toggleWaffle = () => {
    const next = !waffleOpen;
    setWaffleOpen(next);
    setMenuOpen(false);
    setModuleOpen(false);
    if (next) loadApps();
  };

  /* Propagate SSO: append the tokens to the target app URL. */
  const openApp = (app, e, newTab) => {
    if (e) e.preventDefault();
    const token = getToken();
    const refresh = getRefreshToken();
    let url = app.resolvedUrl;
    if (token) {
      const sep = url.includes("?") ? "&" : "?";
      url =
        url +
        sep +
        "access_token=" +
        encodeURIComponent(token) +
        (refresh ? "&refresh_token=" + encodeURIComponent(refresh) : "");
    }
    if (newTab) {
      window.open(url, "_blank");
    } else {
      window.location.href = url;
    }
  };

  const roleLabel = user && user.role ? String(user.role).replace(/_/g, " ") : "";

  return (
    <header className="app-header">
      {/* Brand removed from header — the docked sidebar already shows the Siragu logo,
          so a header copy was redundant. Empty spacer keeps actions right-aligned. */}
      <div className="app-header-brand" />

      {/* Actions (right) */}
      <div className="app-header-actions">
        {/* Utility icons — match the module-suite header style */}
        <button type="button" className="hdr-icon-btn" onClick={toggleTheme} title="Toggle theme" aria-label="Toggle theme">
          <svg width="19" height="19" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
          </svg>
        </button>
        {showSettings && (
          <button
            type="button"
            className="hdr-icon-btn"
            onClick={() => onOpenSettings && onOpenSettings()}
            title="AI Settings"
            aria-label="AI Settings"
          >
            <svg width="19" height="19" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.8" />
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V15z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
            </svg>
          </button>
        )}
        <button type="button" className="hdr-icon-btn" title="Notifications" aria-label="Notifications">
          <svg width="19" height="19" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9M13.7 21a2 2 0 0 1-3.4 0" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>

        {/* Module selector — tells Siragu which ERP app this conversation is about, so
            questions resolve to the right module instead of guessing from keywords alone. */}
        {user && (
          <div className="module-switcher" ref={moduleRef}>
            <button
              type="button"
              className={`module-btn${moduleOpen ? " is-open" : ""}${selectedApp ? " is-set" : ""}`}
              onClick={toggleModule}
              aria-haspopup="true"
              aria-expanded={moduleOpen}
              title="Which app is this conversation about?"
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <rect x="3" y="3" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.8" />
                <rect x="14" y="3" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.8" />
                <rect x="3" y="14" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.8" />
                <rect x="14" y="14" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.8" />
              </svg>
              <span className="module-btn-label">{selectedApp ? selectedApp.name : "Module"}</span>
            </button>

            {moduleOpen && (
              <div className="user-panel module-panel" role="menu">
                <div className="waffle-panel-head">Ask about which app?</div>
                <div className="user-panel-items">
                  <button
                    type="button"
                    className={`user-panel-item${!selectedApp ? " is-selected" : ""}`}
                    onClick={() => pickModule(null)}
                  >
                    General (no specific module)
                  </button>
                  {moduleAppsLoading ? (
                    <div className="waffle-note">Loading…</div>
                  ) : moduleAppsError === "no-token" ? (
                    <div className="waffle-note">Sign in to select a module.</div>
                  ) : moduleAppsError === "fetch-failed" ? (
                    <div className="waffle-note">Couldn't load your apps.</div>
                  ) : (
                    moduleApps.map((app) => (
                      <button
                        type="button"
                        key={app.app_id}
                        className={`user-panel-item${selectedApp && selectedApp.id === app.app_id ? " is-selected" : ""}`}
                        onClick={() => pickModule(app)}
                      >
                        {app.app_name}
                      </button>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* App switcher (waffle) */}
        <div className="app-switcher" ref={waffleRef}>
          <button
            type="button"
            className={`waffle-btn${waffleOpen ? " is-open" : ""}`}
            onClick={toggleWaffle}
            aria-label="Switch apps"
            aria-expanded={waffleOpen}
            title="Your apps"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
              {[4, 12, 20].map((cy) =>
                [4, 12, 20].map((cx) => (
                  <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r="2" fill="currentColor" />
                ))
              )}
            </svg>
          </button>

          {waffleOpen && (
            <div className="waffle-panel" role="menu">
              <div className="waffle-panel-head">Your apps</div>
              {appsLoading ? (
                <div className="waffle-note">Loading…</div>
              ) : appsError === "no-token" ? (
                <a className="waffle-signin" href={IDENTITY_HUB_URL}>
                  Sign in via Identity Hub
                </a>
              ) : appsError === "fetch-failed" ? (
                <div className="waffle-note">Couldn't load your apps.</div>
              ) : apps.length === 0 ? (
                <div className="waffle-note">No other apps available.</div>
              ) : (
                <div className="waffle-grid">
                  {apps.map((app) => (
                    <a
                      key={app.app_id}
                      className="waffle-tile"
                      href={app.resolvedUrl}
                      title={app.app_description || app.app_name}
                      onClick={(e) => openApp(app, e, false)}
                      onContextMenu={(e) => openApp(app, e, true)}
                    >
                      <span className="waffle-tile-icon">
                        {app.app_icon &&
                        String(app.app_icon).startsWith("http") ? (
                          <img src={app.app_icon} alt="" />
                        ) : (
                          <span className="waffle-tile-badge">
                            {(app.app_name || "?").charAt(0).toUpperCase()}
                          </span>
                        )}
                      </span>
                      <span className="waffle-tile-name">{app.app_name}</span>
                    </a>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Institution branding (from Identity Hub SSO) */}
        {(instLogo || instName) && (
          <>
            <span className="hdr-divider" />
            <div className="hdr-inst" title={instName || ""}>
              {instLogo && <img className="hdr-inst-logo" src={instLogo} alt="" />}
              {instName && <span className="hdr-inst-name">{instName}</span>}
            </div>
          </>
        )}
        <span className="hdr-divider" />

        {/* User menu */}
        {user ? (
          <div className="user-menu" ref={menuRef}>
            <button
              type="button"
              className={`avatar-btn${menuOpen ? " is-open" : ""}`}
              onClick={() => {
                setMenuOpen((v) => !v);
                setWaffleOpen(false);
                setModuleOpen(false);
              }}
              aria-label="Account menu"
              aria-expanded={menuOpen}
              title={user.full_name || user.email}
            >
              {initialsOf(user)}
            </button>

            {menuOpen && (
              <div className="user-panel" role="menu">
                <div className="user-panel-head">
                  <div className="user-panel-avatar">{initialsOf(user)}</div>
                  <div className="user-panel-id">
                    <div className="user-panel-name">
                      {user.full_name || user.email}
                    </div>
                    <div className="user-panel-email">{user.email}</div>
                    {roleLabel && (
                      <span className="user-panel-role">{roleLabel}</span>
                    )}
                  </div>
                </div>

                <div className="user-panel-items">
                  <a
                    className="user-panel-item"
                    href={`${IDENTITY_HUB_URL}/profile`}
                  >
                    Profile
                  </a>
                  {showSettings && (
                    <button
                      type="button"
                      className="user-panel-item"
                      onClick={() => {
                        setMenuOpen(false);
                        if (onOpenSettings) onOpenSettings();
                      }}
                    >
                      AI Settings
                    </button>
                  )}
                  <button
                    type="button"
                    className="user-panel-item user-panel-signout"
                    onClick={() => logout()}
                  >
                    Sign out
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <a className="signin-btn" href={IDENTITY_HUB_URL}>
            Sign in
          </a>
        )}
      </div>
    </header>
  );
}

export default AppHeader;
